import React, { useState, useEffect } from 'react';
import { AutoAwesome as PolishIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  type CompetitorItem,
  addCompetitor,
  polishKnowledgeFields,
  updateCompetitor
} from '../services/apiService';
import { CompetitorCategory } from '../types/competitor';
import { Dropdown } from './ui/Dropdown';

const COMPETITOR_TYPE_OPTIONS = [
  { value: 'direct_competitor', label: 'Direct Competitor' },
  { value: 'indirect_competitor', label: 'Indirect Competitor' },
  { value: 'alternative_solution', label: 'Alternative Solution' },
];

interface CompetitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitor: CompetitorItem | null;
  onSuccess: () => void;
  setSuccess: (success: string | null) => void;
}

const CompetitorModal: React.FC<CompetitorModalProps> = ({
  isOpen,
  onClose,
  competitor,
  onSuccess,
  setSuccess
}) => {
  const [formData, setFormData] = useState<Partial<CompetitorItem>>({
    name: '',
    talking_points: '',
    category: 'direct_competitor',
    advantages: [],
    weaknesses: [],
    isActive: true
  });

  const [saving, setSaving] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishFeedback, setPolishFeedback] = useState<string | null>(null);
  const [newAdvantage, setNewAdvantage] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const modalContentRef = React.useRef<HTMLDivElement>(null);

  // Scroll to top when error is set
  useEffect(() => {
    if (localError && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [localError]);

  useEffect(() => {
    if (isOpen) {
      // Reset all state when modal opens
      setNewAdvantage('');
      setNewWeakness('');
      setLocalError(null);
      setPolishFeedback(null);

      if (competitor) {
        setFormData({
          name: competitor.name,
          talking_points: competitor.talking_points,
          category: competitor.category,
          advantages: competitor.advantages || [],
          weaknesses: competitor.weaknesses || [],
          isActive: competitor.isActive
        });
      } else {
        setFormData({
          name: '',
          talking_points: '',
          category: 'direct_competitor',
          advantages: [],
          weaknesses: [],
          isActive: true
        });
      }
    }
  }, [isOpen, competitor]);

  useEffect(() => {
    if (!isOpen || saving) return;
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', dismissOnEscape);
    return () => document.removeEventListener('keydown', dismissOnEscape);
  }, [isOpen, onClose, saving]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!formData.name?.trim()) {
      setLocalError('Competitor name is required');
      return;
    }

    // Validate that at least 1 advantage and 1 weakness are added
    if (!formData.advantages || formData.advantages.length === 0) {
      setLocalError('Please add at least 1 advantage over this competitor');
      return;
    }

    if (!formData.weaknesses || formData.weaknesses.length === 0) {
      setLocalError('Please add at least 1 weakness of this competitor');
      return;
    }

    try {
      setSaving(true);
      if (competitor) {
        await updateCompetitor(competitor._id, formData);
        setSuccess('Competitor updated successfully');
        onSuccess();
        onClose();
      } else {
        await addCompetitor(formData);
        setSuccess('Competitor added successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error saving competitor:', error);

      // Handle duplicate key error specifically
      // Check both error.message and error.error for E11000 or duplicate
      const errorText = error?.error || error?.message || '';
      if (errorText.includes('E11000') ||
          errorText.includes('duplicate key') ||
          errorText.includes('already exists')) {
        setLocalError('A competitor with this name already exists. Please use a different name.');
      } else {
        // Show the actual error message if available, otherwise generic message
        setLocalError(error?.message || error?.error || 'Failed to save competitor');
      }
    } finally {
      setSaving(false);
    }
  };

  const addAdvantage = () => {
    if (newAdvantage.trim()) {
      const newAdvantages = [...(formData.advantages || []), { point: newAdvantage.trim(), category: CompetitorCategory.OTHER }];
      setFormData({
        ...formData,
        advantages: newAdvantages
      });
      setNewAdvantage('');
    }
  };

  const removeAdvantage = (index: number) => {
    setFormData({
      ...formData,
      advantages: formData.advantages?.filter((_, i) => i !== index) || []
    });
  };

  const addWeakness = () => {
    if (newWeakness.trim()) {
      const newWeaknesses = [...(formData.weaknesses || []), { point: newWeakness.trim(), category: CompetitorCategory.OTHER }];
      setFormData({
        ...formData,
        weaknesses: newWeaknesses
      });
      setNewWeakness('');
    }
  };

  const removeWeakness = (index: number) => {
    setFormData({
      ...formData,
      weaknesses: formData.weaknesses?.filter((_, i) => i !== index) || []
    });
  };

  const handlePolish = async () => {
    const fields = [
      ...(formData.talking_points?.trim()
        ? [{ id: 'talking_points', text: formData.talking_points, contentRole: 'agent-talk-track' as const }]
        : []),
      ...(formData.advantages || []).map((advantage, index) => ({
        id: `advantage:${index}`,
        text: advantage.point,
        contentRole: 'agent-talk-track' as const,
      })),
      ...(formData.weaknesses || []).map((weakness, index) => ({
        id: `weakness:${index}`,
        text: weakness.point,
        contentRole: 'agent-talk-track' as const,
      })),
    ].filter((field) => field.text.trim());

    if (fields.length === 0) {
      setPolishFeedback('Add talking points, advantages, or weaknesses before polishing.');
      return;
    }

    setPolishing(true);
    setPolishFeedback(null);
    try {
      const polished = await polishKnowledgeFields('competitor', fields, formData.name ? [formData.name] : []);
      const byId = new Map(polished.map((field) => [field.id, field.text]));
      setFormData((current) => ({
        ...current,
        talking_points: byId.get('talking_points') || current.talking_points,
        advantages: current.advantages?.map((advantage, index) => ({
          ...advantage,
          point: byId.get(`advantage:${index}`) || advantage.point,
        })),
        weaknesses: current.weaknesses?.map((weakness, index) => ({
          ...weakness,
          point: byId.get(`weakness:${index}`) || weakness.point,
        })),
      }));
      setPolishFeedback(
        polished.some((field) => field.changed)
          ? 'Polished wording applied. Review it before saving.'
          : 'This wording is already ready for a live call.'
      );
    } catch (error) {
      console.error('Error polishing competitor:', error);
      setPolishFeedback('Could not polish this content. Your original wording is unchanged.');
    } finally {
      setPolishing(false);
    }
  };

  return (
    <div className="apexia-kb modal-backdrop kb-modal-overlay">
      <div ref={modalContentRef} role="dialog" aria-modal="true" aria-labelledby="competitor-modal-title" data-kb-modal="true" className="modal-panel kb-dialog max-w-2xl w-full max-h-[85vh]">
        <div className="kb-dialog-header sticky top-0 bg-surface flex justify-between items-center">
          <h2 id="competitor-modal-title" className="heading-4 kb-dialog-title">
            {competitor ? 'Edit Competitor' : 'Add Competitor'}
          </h2>
          <button
            type="button"
            aria-label="Close competitor editor"
            onClick={onClose}
            className="text-ink-muted hover:text-ink-secondary"
          >
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="kb-dialog-body space-y-6">
          {/* Error Display */}
          {localError && (
            <div id="competitor-form-error" role="alert" className="bg-error-tint border border-error rounded-lg p-4">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-error mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-ink mb-1">Error</h3>
                  <p className="text-sm text-error">{localError}</p>
                </div>
                <button
                  type="button"
                  aria-label="Dismiss competitor form error"
                  onClick={() => setLocalError(null)}
                  className="text-error hover:opacity-80"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {polishFeedback && (
            <div role="status" className="bg-info-tint border border-info rounded-lg p-3 text-sm text-ink">
              {polishFeedback}
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="competitor-name" className="block text-sm font-medium text-ink-secondary mb-1">
                Competitor Name *
              </label>
              <input
                id="competitor-name"
                type="text"
                aria-invalid={localError === 'Competitor name is required'}
                aria-describedby={localError ? 'competitor-form-error' : undefined}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                disabled={!!competitor}
                className={`w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature ${
                  competitor ? 'bg-surface-subtle cursor-not-allowed' : ''
                }`}
                required
              />
              {competitor && (
                <p className="text-xs text-ink-muted mt-1">
                  Name cannot be changed. Delete and recreate if needed.
                </p>
              )}
            </div>

            <div>
              <label id="competitor-category-label" className="block text-sm font-medium text-ink-secondary mb-1">
                Category
              </label>
              <Dropdown
                labelledBy="competitor-category-label"
                width="field"
                value={formData.category || 'direct_competitor'}
                onChange={(category) => setFormData({ ...formData, category: category as any })}
                options={COMPETITOR_TYPE_OPTIONS}
              />
            </div>
          </div>

          {/* Talking Points */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">
              Talking Points
            </label>
            <textarea
              value={formData.talking_points || ''}
              onChange={(e) => setFormData({ ...formData, talking_points: e.target.value })}
              className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
              rows={3}
              placeholder="Key points to mention when this competitor is detected..."
            />
          </div>

          {/* Advantages */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Our Advantages *
            </label>
            <p className="text-xs text-ink-muted mb-2">At least 1 advantage is required. Press Enter or click Add.</p>
            <div className="space-y-2">
              {formData.advantages?.map((advantage, index) => (
                <div key={index} className="kb-dialog-list-item flex items-center gap-2 p-2 rounded-md">
                  <span className="flex-1 text-sm">{advantage.point}</span>
                  <button
                    type="button"
                    onClick={() => removeAdvantage(index)}
                    className="kb-dialog-remove px-2 py-1 rounded-md text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newAdvantage}
                  onChange={(e) => setNewAdvantage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      addAdvantage();
                    }
                  }}
                  placeholder="Add an advantage over this competitor... (Press Enter)"
                  className="flex-1 px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    addAdvantage();
                  }}
                  className="kb-dialog-compact-action px-4 py-2 rounded-md"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Weaknesses */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-2">
              Their Weaknesses *
            </label>
            <p className="text-xs text-ink-muted mb-2">At least 1 weakness is required. Press Enter or click Add.</p>
            <div className="space-y-2">
              {formData.weaknesses?.map((weakness, index) => (
                <div key={index} className="kb-dialog-list-item is-caution flex items-center gap-2 p-2 rounded-md">
                  <span className="flex-1 text-sm">{weakness.point}</span>
                  <button
                    type="button"
                    onClick={() => removeWeakness(index)}
                    className="kb-dialog-remove px-2 py-1 rounded-md text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newWeakness}
                  onChange={(e) => setNewWeakness(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      addWeakness();
                    }
                  }}
                  placeholder="Add a weakness of this competitor... (Press Enter)"
                  className="flex-1 px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    addWeakness();
                  }}
                  className="kb-dialog-compact-action px-4 py-2 rounded-md"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive !== false}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="h-4 w-4 text-signature focus:ring-signature border-ink-border rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-ink">
              Active (Enable detection for this competitor)
            </label>
          </div>

          {/* Form Actions */}
          <div className="kb-dialog-actions flex justify-between gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handlePolish}
              disabled={saving || polishing}
              className="kb-button kb-button-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {polishing ? 'Polishing...' : (
                <><PolishIcon style={{ fontSize: '1rem' }} /> Polish with AI</>
              )}
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving || polishing}
                className="kb-button kb-button-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || polishing}
                className="kb-button kb-button-primary px-4 py-2"
              >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {competitor ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{competitor ? 'Update' : 'Create'} Competitor</>
              )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompetitorModal;
